import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Stack } from '@mui/material';
import { useAtom } from 'jotai';
import PropTypes from 'prop-types';

import { useUserContext } from 'src/components/app/context/UserContext';
import { usePlanActionContext } from 'src/components/planAction/PlanActionContext';
import { actionTargetStatementAtom } from 'src/components/planAction/PlanActionDetailPage/components/Action/atoms';

import { styles } from './index.styles';

const getDifferenceString = (selected, previous) => {
  if (
    !selected ||
    typeof selected.fav !== 'number' ||
    !previous ||
    typeof previous.fav !== 'number'
  )
    return '-';
  const value = selected.fav - previous.fav;
  return `${value > 0 ? '+' : ''}${value}`;
};

/**
 *
 * @param kpis {{
 * fav: number|null,
 * has_actionplan: boolean,
 * id: number,
 * import_id: string,
 * label: string,
 * year: number,
 * }[]}
 * @param targetStatements {{
 * fav: number|null,
 * kpi_id: number,
 * id: number,
 * label: string,
 * year: number,
 * }[]}
 * @returns {{}|{
 * statements: {fav: number|null, label: string, difference: number|string, id: number}[],
 * currentKpi: {fav: number|null, label: string, difference: number|string},
 * }}
 * @param selectedKpiId {number}
 * @param currentYear {number}
 */
export const generateStatementResults = (
  kpis,
  targetStatements,
  selectedKpiId,
  currentYear,
) => {
  if (!kpis || !targetStatements) return {};
  const selectedKpi = kpis.find(
    (k) => k.id === selectedKpiId && k.year === currentYear,
  );
  if (!selectedKpi) return {};
  const previousKpi = kpis.find(
    (k) => k.id === selectedKpiId && k.year === selectedKpi.year - 1,
  );
  const currentKpi = {
    label: selectedKpi.label,
    fav: selectedKpi.fav,
    difference: getDifferenceString(selectedKpi, previousKpi),
  };

  const selectedStatements = targetStatements.filter(
    ({ kpi_id, year }) => kpi_id === selectedKpiId && year === selectedKpi.year,
  );
  const previousStatements = targetStatements.filter(
    ({ kpi_id, year }) =>
      kpi_id === selectedKpi.id && year === selectedKpi.year - 1,
  );

  const statements = selectedStatements.map((statement) => {
    const previousStatement = previousStatements.find(
      (p) => p.id === statement.id,
    );
    return {
      fav: statement.fav,
      label: statement.label,
      id: statement.id,
      difference: getDifferenceString(statement, previousStatement),
    };
  });

  return {
    statements,
    currentKpi,
  };
};

const TargetStatementResults = ({
  actionId,
  kpi,
  selectedTargetStatement = -1,
}) => {
  const { t } = useTranslation('planAction');
  const { planActionStatus } = usePlanActionContext();
  const { currentSurvey } = useUserContext();

  const { currentKpi, statements } = useMemo(() => {
    if (!planActionStatus || !planActionStatus.value) {
      return {};
    }
    return generateStatementResults(
      planActionStatus.value.kpis,
      planActionStatus.value.target_statements,
      kpi,
      currentSurvey.year,
    );
  }, [kpi, planActionStatus, currentSurvey]);

  const [targetStatement] = useAtom(actionTargetStatementAtom(actionId));

  if (!currentKpi || !statements) return null;

  return (
    <Stack>
      <Stack direction="row" sx={{ py: 1, px: 1 }}>
        <Box sx={{ width: '60%' }} />
        <Box sx={{ width: '20%', textAlign: 'center', fontWeight: 'bold' }}>
          {t('ActionPlan.Favorable')}
        </Box>
        <Box
          sx={{
            width: '20%',
            textAlign: 'center',
            fontWeight: 'bold',
          }}
        >
          {t('ActionPlan.Diff', { year: currentSurvey.year - 1 })}
        </Box>
      </Stack>
      <Stack
        direction="row"
        sx={styles.row(
          selectedTargetStatement === -1 && targetStatement === null,
        )}
      >
        <Box sx={{ width: '60%' }}>{currentKpi.label}</Box>
        <Box width="20%" textAlign="center">
          {currentKpi.fav === null
            ? `n < ${currentSurvey.minRespondentAmount} / NegSel`
            : currentKpi.fav}
        </Box>
        <Box sx={{ width: '20%', textAlign: 'center' }}>
          {currentKpi.difference}
        </Box>
      </Stack>
      {statements.map(({ fav, label, id, difference }) => (
        <Stack
          direction="row"
          key={label}
          sx={styles.row(
            selectedTargetStatement === -1
              ? id === targetStatement
              : id === selectedTargetStatement,
          )}
        >
          <Box sx={{ width: '60%' }}>{label}</Box>
          <Box width="20%" textAlign="center">
            {fav === null
              ? `n < ${currentSurvey.minRespondentAmount} / NegSel`
              : fav}
          </Box>
          <Box sx={{ width: '20%', textAlign: 'center' }}>{difference}</Box>
        </Stack>
      ))}
    </Stack>
  );
};

TargetStatementResults.propTypes = {
  actionId: PropTypes.number.isRequired,
  kpi: PropTypes.number.isRequired,
  selectedTargetStatement: PropTypes.number,
};

export default TargetStatementResults;
